import { useState, useEffect } from "react";
import {
  Box, Typography, Grid, IconButton,
  Chip, Stack, Container,
} from "@mui/material";
import { createTheme, ThemeProvider, alpha } from "@mui/material/styles";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";
import YouTubeIcon from "@mui/icons-material/YouTube";
import PinterestIcon from "@mui/icons-material/Pinterest";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import StorefrontIcon from "@mui/icons-material/Storefront";

// Import store badge images
import appStoreBadge from "../../../assets/images/App store.svg"; // or .png
import googlePlayBadge from "../../../assets/images/google play.png"; // or .webp

// ── Palette ─────────────────────────────────────────────────────
const C = {
  bg: "#f3de9f",
  navy: "#001f54",
  gold: "#FFD166",
  navyStrong: "#001133",
  navyMid: alpha("#001f54", 0.75),
  navyLight: alpha("#001f54", 0.45),
  navyGhost: alpha("#001f54", 0.12),
};

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: C.navy },
    secondary: { main: C.gold },
    background: { default: C.bg, paper: C.bg },
  },
  typography: { fontFamily: '"DM Sans", "Segoe UI", sans-serif' },
});

// ── Keyframe injection ───────────────────────────────────────────
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');

    @keyframes shimmer {
      0%   { background-position: -200% center; }
      100% { background-position:  200% center; }
    }
    @keyframes pulseDot {
      0%,100% { box-shadow: 0 0 0 0 ${C.gold}88; }
      50%     { box-shadow: 0 0 0 6px transparent; }
    }
    @keyframes borderSpin {
      to { background-position: 200% center; }
    }
  `}</style>
);

// ── Trust Badge ──────────────────────────────────────────────────
const TrustBadge = ({ icon, title, subtitle }) => (
  <Box sx={{
    display: "flex", alignItems: "center", gap: 1.8,
    px: 2.5, py: 2, borderRadius: "18px",
    background: "rgba(0,31,84,0.06)",
    border: `1.5px solid rgba(0,31,84,0.13)`,
    flex: 1, minWidth: 160,
    cursor: "default",
    transition: "all 0.32s cubic-bezier(.4,0,.2,1)",
    "&:hover": {
      background: C.navy,
      border: `1.5px solid ${C.navy}`,
      transform: "translateY(-5px) scale(1.02)",
      boxShadow: `0 16px 36px rgba(0,31,84,0.22)`,
      "& .bi": { color: C.gold },
      "& .bt": { color: "#fff" },
      "& .bs": { color: "rgba(255,255,255,0.62)" },
    },
  }}>
    <Box className="bi" sx={{
      color: C.navy, display: "flex", flexShrink: 0,
      "& svg": { fontSize: 30 },
      transition: "color 0.3s",
    }}>
      {icon}
    </Box>
    <Box>
      <Typography className="bt" sx={{
        fontWeight: 800, fontSize: "0.82rem", color: C.navy,
        lineHeight: 1.2, transition: "color 0.3s",
      }}>
        {title}
      </Typography>
      <Typography className="bs" sx={{
        fontSize: "0.68rem", color: C.navyMid,
        lineHeight: 1.5, mt: 0.2, transition: "color 0.3s",
      }}>
        {subtitle}
      </Typography>
    </Box>
  </Box>
);

// ── Social Button ────────────────────────────────────────────────
const SocialBtn = ({ icon, label }) => (
  <IconButton aria-label={label} sx={{
    width: 42, height: 42, borderRadius: "12px",
    background: "rgba(0,31,84,0.08)",
    border: `1.5px solid rgba(0,31,84,0.16)`,
    color: C.navy,
    transition: "all 0.28s cubic-bezier(.4,0,.2,1)",
    "&:hover": {
      background: C.navy,
      border: `1.5px solid ${C.navy}`,
      color: C.gold,
      transform: "translateY(-5px) rotate(-5deg)",
      boxShadow: `0 10px 28px rgba(0,31,84,0.28)`,
    },
  }}>
    {icon}
  </IconButton>
);

// ── App Store Badge Component ──
const AppStoreBadge = () => (
  <Box
    component="a"
    href="https://apps.apple.com/your-app-id"
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      display: "inline-block",
      transition: "all 0.25s",
      cursor: "pointer",
      "&:hover": {
        transform: "translateY(-3px)",
        filter: "drop-shadow(0 8px 22px rgba(0,31,84,0.28))",
      },
      "& img": {
        height: "50px",
        width: "auto",
        display: "block",
      }
    }}
  >
    <img 
      src={appStoreBadge} 
      alt="Download on the App Store"
      style={{ height: "50px", width: "auto" }}
    />
  </Box>
);

// ── Google Play Badge Component ──
const GooglePlayBadge = () => (
  <Box
    component="a"
    href="https://play.google.com/store/apps/details?id=your.app.id"
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      display: "inline-block",
      transition: "all 0.25s",
      cursor: "pointer",
      "&:hover": {
        transform: "translateY(-3px)",
        filter: "drop-shadow(0 8px 22px rgba(0,31,84,0.28))",
      },
      "& img": {
        height: "50px",
        width: "auto",
        display: "block",
      }
    }}
  >
    <img 
      src={googlePlayBadge} 
      alt="Get it on Google Play"
      style={{ height: "50px", width: "auto" }}
    />
  </Box>
);

// ── Footer Nav Section ───────────────────────────────────────────
const FooterSection = ({ title, items }) => (
  <Box>
    <Typography sx={{
      fontWeight: 800, fontSize: "0.75rem",
      textTransform: "uppercase", letterSpacing: "2.5px",
      color: C.navy, mb: 2.5,
      display: "flex", alignItems: "center", gap: 1.5,
      "&::after": {
        content: '""', flex: 1, height: "2.5px",
        background: `linear-gradient(90deg, ${C.gold} 0%, transparent 100%)`,
        borderRadius: "3px",
      },
    }}>
      {title}
    </Typography>
    <Stack spacing={1.5}>
      {items.map((item) => (
        <Typography key={item} sx={{
          fontSize: "0.86rem",
          fontWeight: 600,
          color: C.navyMid,
          cursor: "default",
          transition: "all 0.22s ease",
          "&:hover": { color: C.navy, paddingLeft: "8px" },
        }}>
          {item}
        </Typography>
      ))}
    </Stack>
  </Box>
);

// ── Main Component ───────────────────────────────────────────────
export default function ZippShoppeFooter() {
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const fn = () => setShowTop(window.scrollY > 300);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const categories = ["Electronics", "Fashion", "Home & Living", "Sports", "Beauty", "Grocery"];

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <Box component="footer" sx={{
        background: C.bg,
        position: "relative", overflow: "hidden",
        fontFamily: '"DM Sans", sans-serif',
        marginTop: 4,
      }}>

        {/* ── Top animated border ── */}
        <Box sx={{
          position: "absolute", top: 0, left: 0, right: 0, height: "4px",
          background: `linear-gradient(90deg, ${C.navy} 0%, ${C.gold} 35%, ${C.navy} 65%, ${C.gold} 100%)`,
          backgroundSize: "200% auto",
          animation: "borderSpin 4s linear infinite",
          zIndex: 2,
        }} />

        {/* ── Decorative backgrounds ── */}
        <Box sx={{
          position: "absolute", width: 420, height: 420, borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(C.gold, 0.28)} 0%, transparent 72%)`,
          top: -180, right: -60, pointerEvents: "none",
        }} />
        <Box sx={{
          position: "absolute", width: 320, height: 320, borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(C.navy, 0.09)} 0%, transparent 70%)`,
          bottom: -120, left: -40, pointerEvents: "none",
        }} />
        <Box sx={{
          position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0,
          opacity: 0.055,
          backgroundImage: `radial-gradient(circle, ${C.navy} 1.2px, transparent 1.2px)`,
          backgroundSize: "30px 30px",
        }} />

        {/* ── Trust Badges ── */}
        <Box sx={{
          borderBottom: `2px solid rgba(0,31,84,0.1)`,
          py: 3.5, position: "relative", zIndex: 1,
        }}>
          <Container maxWidth="lg">
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap
              justifyContent={{ xs: "center", md: "space-between" }}>
              <TrustBadge icon={<LocalShippingOutlinedIcon />} title="Free Delivery" subtitle="On orders above ₹499" />
              <TrustBadge icon={<VerifiedOutlinedIcon />} title="100% Genuine" subtitle="Verified products only" />
              <TrustBadge icon={<CurrencyRupeeIcon />} title="Easy Returns" subtitle="7-day hassle-free returns" />
              <TrustBadge icon={<HeadsetMicOutlinedIcon />} title="24/7 Support" subtitle="Always here to help" />
            </Stack>
          </Container>
        </Box>

        {/* ── Main Body ── */}
        <Container maxWidth="lg" sx={{ py: 8, position: "relative", zIndex: 1 }}>
          <Grid container spacing={{ xs: 5, md: 6 }}>

            {/* ── Brand Column ── */}
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{
                  fontWeight: 900,
                  fontSize: "1.9rem",
                  lineHeight: 1,
                  letterSpacing: "-1px",
                  mb: 0.6,
                  background: `linear-gradient(90deg, ${C.navy} 0%, ${C.navyStrong} 40%, ${C.gold} 55%, ${C.navy} 70%)`,
                  backgroundSize: "200% auto",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  animation: "shimmer 4s linear infinite",
                }}>
                  Zippshoppe
                </Typography>
                <Typography sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  color: C.navyMid,
                  letterSpacing: "3px",
                  textTransform: "uppercase",
                }}>
                  Shop Easy. Shop Smarter.
                </Typography>
              </Box>

              <Typography sx={{
                mb: 3,
                fontSize: "0.87rem",
                fontWeight: 500,
                color: C.navy,
                lineHeight: 1.85,
                maxWidth: 315,
                opacity: 0.78,
                ml: 8
              }}>
               Revolutionary hybrid commerce with innovative multi-store solutions. Connecting buyers and sellers in a seamless, trustworthy digital marketplace.
              </Typography>

              {/* Tagline pill */}
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: 1,
                px: 2.2, py: 0.9, borderRadius: "40px",
                background: C.navy, mb: 3,
                boxShadow: `0 4px 16px rgba(0,31,84,0.25)`,
              }}>
                <StorefrontIcon sx={{ color: C.gold, fontSize: 16 }} />
                <Typography sx={{
                  fontSize: "0.7rem", fontWeight: 800,
                  color: C.gold, letterSpacing: "1.5px",
                  textTransform: "uppercase",
                }}>
                  Happy Shoppers
                </Typography>
              </Box>

              {/* Category chips */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.9, mb: 3.5 }}>
                {categories.map((cat) => (
                  <Chip key={cat} label={cat} size="small" sx={{
                    fontSize: "0.66rem", fontWeight: 700, height: 26,
                    background: "rgba(0,31,84,0.08)",
                    border: `1.5px solid rgba(0,31,84,0.18)`,
                    color: C.navy,
                    cursor: "default",
                    transition: "all 0.22s",
                    "& .MuiChip-label": { px: 1.2 },
                    "&:hover": {
                      background: C.navy,
                      color: C.gold,
                      border: `1.5px solid ${C.navy}`,
                      transform: "scale(1.06)",
                    },
                  }} />
                ))}
              </Box>

              {/* Social icons */}
              <Box>
                <Typography sx={{
                  fontSize: "0.68rem", fontWeight: 700,
                  color: C.navy, opacity: 0.55,
                  textTransform: "uppercase", letterSpacing: "2px", mb: 1.5,
                }}>
                  Follow us
                </Typography>
                <Stack direction="row" spacing={1} ml={13}>
                  <SocialBtn icon={<InstagramIcon sx={{ fontSize: 19 }} />} label="Instagram" />
                  <SocialBtn icon={<FacebookIcon sx={{ fontSize: 19 }} />} label="Facebook" />
                  <SocialBtn icon={<TwitterIcon sx={{ fontSize: 19 }} />} label="Twitter" />
                  <SocialBtn icon={<YouTubeIcon sx={{ fontSize: 19 }} />} label="YouTube" />
                  <SocialBtn icon={<PinterestIcon sx={{ fontSize: 19 }} />} label="Pinterest" />
                </Stack>
              </Box>
            </Grid>

            {/* ── Company ── */}
            <Grid item xs={6} sm={3} md={2}>
              <FooterSection title="Company"
                items={["About Us", "Careers", "Press", "Blog", "Partners"]} />
            </Grid>

            {/* ── Support ── */}
            <Grid item xs={6} sm={3} md={2}>
              <FooterSection title="Support"
                items={["Help Center", "Track Order"]} />
            </Grid>

            {/* ── App Download ── */}
            <Grid item xs={12} sm={6} md={4} ml={5}>
              <Typography sx={{
                fontWeight: 800, fontSize: "0.75rem",
                textTransform: "uppercase", letterSpacing: "2.5px",
                color: C.navy, mb: 2.5,
                display: "flex", alignItems: "center", gap: 1.5,
                "&::after": {
                  content: '""', flex: 1, height: "2.5px",
                  background: `linear-gradient(90deg, ${C.navy} 0%, transparent 100%)`,
                  borderRadius: "3px",
                },
              }}>
                Download the App
              </Typography>
              
              {/* Store Badges */}
              <Stack direction="row" spacing={2} alignItems="center">
                <AppStoreBadge />
                <GooglePlayBadge />
              </Stack>
            </Grid>

          </Grid>
        </Container>

        {/* ── Divider with icon badge ── */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{
            height: "1.5px",
            background: `linear-gradient(90deg, transparent 0%, ${C.navy} 20%, ${C.gold} 50%, ${C.navy} 80%, transparent 100%)`,
            opacity: 0.25,
          }} />
          <Box sx={{
            position: "absolute", top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            background: C.bg, px: 2,
          }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: "9px",
              background: C.navy,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: `0 4px 14px rgba(0,31,84,0.2)`,
            }}>
              <StorefrontIcon sx={{ color: C.gold, fontSize: 16 }} />
            </Box>
          </Box>
        </Box>

        {/* ── Bottom Bar ── */}
        <Box sx={{ py: 3, position: "relative", zIndex: 1 }}>
          <Container maxWidth="lg">
            <Box sx={{
              display: "flex", justifyContent: "space-between",
              alignItems: "center", flexWrap: "wrap", gap: 2,
            }}>
              <Typography sx={{
                fontSize: "0.78rem", fontWeight: 600,
                color: C.navy, opacity: 0.6,
              }}>
                © {new Date().getFullYear()}{" "}
                <strong style={{ opacity: 1, color: C.navy }}>Zippshoppe</strong>{" "}
                All rights reserved.
              </Typography>

              <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                {["Privacy Policy", "Terms of Use", "Cookie Policy"].map((item) => (
                  <Typography key={item} sx={{
                    fontSize: "0.76rem", fontWeight: 600,
                    color: C.navy, opacity: 0.55,
                    cursor: "default", transition: "all 0.2s",
                    "&:hover": { opacity: 1 },
                  }}>
                    {item}
                  </Typography>
                ))}
              </Stack>

              {/* Live status */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: C.navy,
                  animation: "pulseDot 2s infinite",
                }} />
                <Typography sx={{
                  fontSize: "0.74rem", fontWeight: 600,
                  color: C.navy, opacity: 0.6,
                }}>
                  All systems operational
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* ── Scroll to top ── */}
        <Box sx={{
          position: "fixed", bottom: 28, right: 28,
          opacity: showTop ? 1 : 0,
          transform: showTop ? "translateY(0) scale(1)" : "translateY(20px) scale(0.8)",
          transition: "all 0.35s cubic-bezier(.4,0,.2,1)",
          zIndex: 9999, pointerEvents: showTop ? "auto" : "none",
        }}>
          <IconButton
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            sx={{
              width: 50, height: 50, borderRadius: "15px",
              background: C.navy, color: C.gold,
              boxShadow: `0 6px 24px rgba(0,31,84,0.35)`,
              border: `2px solid ${alpha(C.gold, 0.3)}`,
              transition: "all 0.28s ease",
              "&:hover": {
                background: C.gold, color: C.navy,
                transform: "translateY(-5px)",
                boxShadow: `0 12px 32px ${alpha(C.gold, 0.5)}`,
                border: `2px solid ${C.gold}`,
              },
            }}
          >
            <KeyboardArrowUpIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Box>

      </Box>
    </ThemeProvider>
  );
}